import { isValidApiResponse, isValidApiResponseWithData, isNonEmptyArray, isObject, isValidNumber, isNonEmptyString } from '../utils/type-guards.utils';
import { ApiResponse } from '../../quotes/interfaces/quote-request.interface';

/**
 * Validadores runtime para estructuras de datos comunes
 * Valida datos del API en tiempo de ejecución antes de usarlos
 */

/**
 * Valida una respuesta de API genérica
 * @param response - Respuesta del API a validar
 * @param context - Contexto para mensajes de error
 * @returns true si es válida, lanza error si no
 * @throws Error si la respuesta no es válida
 */
export function validateApiResponse<T>(
  response: unknown,
  context: string = 'API response'
): asserts response is ApiResponse<T> {
  if (!isValidApiResponse<T>(response)) {
    throw new Error(`${context} tiene una estructura inválida. Se esperaba un objeto con propiedad 'succeeded' de tipo boolean.`);
  }
}

/**
 * Valida que una respuesta de API tenga datos (succeeded: true)
 * @param response - Respuesta del API a validar
 * @param context - Contexto para mensajes de error
 * @returns true si tiene datos, lanza error si no
 * @throws Error si la respuesta no tiene datos
 */
export function validateApiResponseWithData<T>(
  response: unknown,
  context: string = 'API response'
): asserts response is ApiResponse<T> & { succeeded: true; data: T } {
  validateApiResponse<T>(response, context);
  
  if (!isValidApiResponseWithData(response)) {
    // Si la propiedad succeeded es false, intentar obtener el mensaje original del API
    const apiMessage = (response as any)?.message;
    const errorMsg = apiMessage 
      ? apiMessage 
      : `${context} no contiene datos válidos. La propiedad 'succeeded' es false o falta la propiedad 'data'.`;
    throw new Error(errorMsg);
  }
}

/**
 * Valida que un array de datos del API sea válido
 * @param response - Respuesta del API que debe contener un array
 * @param context - Contexto para mensajes de error
 * @returns true si es válido, lanza error si no
 * @throws Error si la respuesta no contiene un array válido
 */
export function validateApiResponseArray<T>(
  response: unknown,
  context: string = 'API response'
): asserts response is ApiResponse<T[]> & { succeeded: true; data: T[] } {
  validateApiResponseWithData<T[]>(response, context);
  
  if (!isNonEmptyArray(response.data)) {
    // Array vacío es válido, solo verificamos que sea un array
    if (!Array.isArray(response.data)) {
      throw new Error(`${context} contiene datos que no son un array.`);
    }
  }
}

/**
 * Valida propiedades requeridas en un objeto
 * @param obj - Objeto a validar
 * @param requiredProps - Array de nombres de propiedades requeridas
 * @param context - Contexto para mensajes de error
 * @throws Error si faltan propiedades requeridas
 */
export function validateRequiredProperties(
  obj: unknown,
  requiredProps: string[],
  context: string = 'Object'
): asserts obj is Record<string, unknown> {
  if (!isObject(obj)) {
    throw new Error(`${context} no es un objeto válido.`);
  }
  
  const missingProps = requiredProps.filter(prop => !(prop in obj));
  
  if (missingProps.length > 0) {
    throw new Error(`${context} falta las siguientes propiedades requeridas: ${missingProps.join(', ')}`);
  }
}

/**
 * Valida que un valor sea un número positivo
 * @param value - Valor a validar
 * @param context - Contexto para mensajes de error
 * @throws Error si el valor no es un número positivo
 */
export function validatePositiveNumber(
  value: unknown,
  context: string = 'Value'
): asserts value is number {
  if (!isValidNumber(value) || value <= 0) {
    throw new Error(`${context} debe ser un número positivo. Valor recibido: ${value}`);
  }
}

/**
 * Valida que un valor sea un string no vacío
 * @param value - Valor a validar
 * @param context - Contexto para mensajes de error
 * @throws Error si el valor no es un string no vacío
 */
export function validateNonEmptyString(
  value: unknown,
  context: string = 'Value'
): asserts value is string {
  if (!isNonEmptyString(value)) {
    throw new Error(`${context} debe ser un string no vacío. Valor recibido: ${typeof value}`);
  }
}
